/*
  LetterTemplatesSettings — CRUD for custom letter templates.

  Two built-ins ship in code (GP Care Plan Update, NDIS Treatment
  Report) and cannot be edited or deleted — but they CAN be duplicated
  into a custom template that the operator is free to tweak. Common
  use-case: create a WorkCover / DVA / TAC letter with the exact
  header block and clinical framing your insurer expects.

  Each template holds:
    * name                  — shown in the compose picker
    * description           — optional operator note
    * recipient_type        — "gp" | "ndis" | "custom" (controls the
                              recipient section on the compose screen)
    * default_sources       — pre-toggled data sources when a new letter
                              of this type is composed
    * header_fields[]       — {id, label} pairs; each becomes an editable
                              field on the compose screen for insurer /
                              claim numbers etc.
    * system_prompt         — the instructions the AI actually receives
    * user_prompt_template  — filled with {client_name}, {extra_notes},
                              {recipient_greeting}, {diagnosis_hint}
*/
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Files, Plus, Pencil, Trash2, Copy, Save, X, Loader2, Lock,
} from "lucide-react";
import { cn } from "@/lib/utils";

const EMPTY = {
  name: "",
  description: "",
  recipient_type: "custom",
  default_sources: { intake: false, soap_notes: true, questionnaires: true, sbn: true, appointments: true },
  header_fields: [],
  system_prompt: "",
  user_prompt_template:
    "Please draft a clinical letter for {client_name} addressed to {recipient_greeting}. Emphasise: {extra_notes}.",
};

export default function LetterTemplatesSettings() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {mode, template}

  async function refresh() {
    setLoading(true);
    try {
      const r = await api.get("/letters/templates");
      setTemplates(r.data.templates || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); }, []);

  async function openForEdit(t) {
    // Fetch the full row so we get system_prompt + user_prompt_template.
    // Built-ins are now editable too — they get saved as overrides at
    // the same id in `letter_templates_custom` on the backend.
    try {
      const r = await api.get(`/letters/templates/${t.id}`);
      setEditing({ mode: "edit", template: r.data });
    } catch (e) {
      toast.error("Failed to load template", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }

  async function duplicate(t) {
    try {
      const r = await api.post(`/letters/templates/${t.id}/duplicate`);
      toast.success(`Duplicated as "${r.data.name}"`);
      await refresh();
      setEditing({ mode: "edit", template: r.data });
    } catch (e) {
      toast.error("Duplicate failed", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }

  async function remove(t) {
    // Two cases handled by the same endpoint:
    //   - Genuine custom template  → soft-delete.
    //   - Customized built-in       → drop the override (revert to shipped).
    const isReset = t.is_builtin && t.is_customized;
    const prompt = isReset
      ? `Reset "${t.name}" to the shipped default? Your customisations will be discarded.`
      : `Delete template "${t.name}"?`;
    // A truly untouched built-in has nothing to delete.
    if (t.is_builtin && !t.is_customized) return;
    if (!window.confirm(prompt)) return;
    try {
      await api.delete(`/letters/templates/${t.id}`);
      toast.success(isReset ? "Reset to shipped defaults" : "Deleted");
      await refresh();
    } catch (e) {
      toast.error(isReset ? "Reset failed" : "Delete failed", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }

  const builtins = templates.filter((t) => t.is_builtin);
  const custom = templates.filter((t) => !t.is_builtin);

  return (
    <Card
      className="p-6 rounded-lg border border-zinc-200 shadow-none"
      data-testid="letter-templates-settings"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            <Files className="h-3.5 w-3.5" /> Letter templates
          </div>
          <h3 className="mt-1 font-display text-xl tracking-tight text-zinc-950">
            Letter template library
          </h3>
          <p className="mt-1 text-xs text-zinc-600 max-w-xl leading-relaxed">
            Two built-in templates ship out of the box. Create your own for
            insurers you write to often (WorkCover, DVA, TAC) with the exact
            header block and clinical framing they expect. Duplicate a
            built-in to get a head start.
          </p>
        </div>
        <Button
          onClick={() => setEditing({ mode: "new", template: { ...EMPTY } })}
          className="bg-zinc-950 hover:bg-zinc-800 text-white"
          data-testid="letter-tpl-add-btn"
        >
          <Plus className="h-4 w-4 mr-1.5" /> New template
        </Button>
      </div>

      {loading ? (
        <div className="mt-6 text-center text-sm text-zinc-500 py-6">
          <Loader2 className="h-4 w-4 inline animate-spin mr-2" /> Loading templates…
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <TemplateGroup title="Built-in" items={builtins} onEdit={openForEdit} onDup={duplicate} onDel={remove} />
          <TemplateGroup title="Your custom templates" items={custom} onEdit={openForEdit} onDup={duplicate} onDel={remove} emptyLabel="No custom templates yet — duplicate a built-in or tap New template to start." />
        </div>
      )}

      {editing && (
        <EditorDialog
          state={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            await refresh();
            setEditing(null);
          }}
        />
      )}
    </Card>
  );
}

function TemplateGroup({ title, items, onEdit, onDup, onDel, emptyLabel }) {
  return (
    <div>
      <div className="text-2xs uppercase tracking-[0.15em] text-zinc-500 font-semibold mb-2">
        {title}
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-zinc-500 italic px-1">{emptyLabel}</p>
      ) : (
        <ul className="divide-y divide-zinc-100 rounded-md border border-zinc-200">
          {items.map((t) => (
            <li
              key={t.id}
              className="p-3 flex items-center gap-3 flex-wrap hover:bg-zinc-50"
              data-testid={`letter-tpl-row-${t.id}`}
            >
              <div className="flex-1 min-w-[220px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-zinc-900 text-sm">{t.name}</span>
                  {t.is_builtin && (
                    <span className="inline-flex items-center gap-1 text-2xs px-1.5 py-0.5 rounded-full bg-zinc-100 border border-zinc-200 text-zinc-600">
                      <Lock className="h-2.5 w-2.5" /> built-in
                    </span>
                  )}
                  {t.is_customized && (
                    <span
                      className="inline-flex items-center gap-1 text-2xs px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-900"
                      title="This built-in template has local edits — a Reset action will restore the shipped defaults."
                    >
                      Customized
                    </span>
                  )}
                  <span className="text-2xs px-1.5 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800 uppercase tracking-wide">
                    {t.recipient_type}
                  </span>
                </div>
                <div className="text-2xs text-zinc-500 mt-0.5 leading-relaxed">
                  {t.description || <span className="italic">No description</span>}
                  {t.header_fields?.length > 0 && (
                    <> · <span className="text-zinc-700">{t.header_fields.length} header field{t.header_fields.length === 1 ? "" : "s"}</span></>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  size="sm" variant="ghost" className="h-7 text-xs"
                  onClick={() => onEdit(t)}
                  data-testid={`letter-tpl-edit-${t.id}`}
                  title="Edit template"
                >
                  <Pencil className="h-3 w-3 mr-1" /> Edit
                </Button>
                <Button
                  size="sm" variant="ghost" className="h-7 text-xs"
                  onClick={() => onDup(t)}
                  data-testid={`letter-tpl-dup-${t.id}`}
                >
                  <Copy className="h-3 w-3 mr-1" /> Duplicate
                </Button>
                {/* Delete for custom templates, Reset for customized built-ins,
                    nothing for pristine built-ins. */}
                {(!t.is_builtin || t.is_customized) && (
                  <Button
                    size="sm" variant="ghost"
                    className="h-7 text-xs text-red-700 hover:text-red-800 hover:bg-red-50"
                    onClick={() => onDel(t)}
                    data-testid={`letter-tpl-del-${t.id}`}
                    title={t.is_builtin ? "Reset to shipped default" : "Delete template"}
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    {t.is_builtin ? "Reset" : ""}
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EditorDialog({ state, onClose, onSaved }) {
  const isNew = state.mode === "new";
  const isBuiltin = !!state.template?.is_builtin;
  const isCustomized = !!state.template?.is_customized;
  const [form, setForm] = useState({ ...EMPTY, ...state.template });
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm({ ...EMPTY, ...state.template }); }, [state.template]);

  function updateField(k, v) {
    setForm({ ...form, [k]: v });
  }
  function updateSource(k, v) {
    setForm({ ...form, default_sources: { ...form.default_sources, [k]: v } });
  }
  function addHeaderField() {
    setForm({
      ...form,
      header_fields: [...(form.header_fields || []), { id: "", label: "" }],
    });
  }
  function updateHeaderField(idx, patch) {
    const rows = [...(form.header_fields || [])];
    rows[idx] = { ...rows[idx], ...patch };
    setForm({ ...form, header_fields: rows });
  }
  function removeHeaderField(idx) {
    const rows = [...(form.header_fields || [])];
    rows.splice(idx, 1);
    setForm({ ...form, header_fields: rows });
  }

  async function save() {
    if (!form.name.trim()) return toast.error("Give the template a name first.");
    if (!form.system_prompt.trim()) return toast.error("System prompt is required — it's what tells the AI how to write.");
    setSaving(true);
    try {
      // Auto-generate header field IDs from labels if the operator left them blank
      const cleaned = {
        ...form,
        header_fields: (form.header_fields || [])
          .filter((f) => (f.label || "").trim())
          .map((f) => ({
            label: f.label.trim(),
            id: (f.id || "").trim() || f.label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, ""),
          })),
      };
      if (isNew) {
        await api.post("/letters/templates", cleaned);
      } else {
        await api.patch(`/letters/templates/${form.id}`, cleaned);
      }
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="letter-tpl-dialog">
        <DialogHeader>
          <DialogTitle>
            {isNew ? "New letter template" : `Edit: ${form.name}`}
          </DialogTitle>
          <DialogDescription>
            {isBuiltin ? (
              <>
                Editing a built-in template — changes are stored as a local override.
                {isCustomized
                  ? " You can hit Reset in the template list to revert to the shipped defaults."
                  : " Save to create a personalised copy; the original ships alongside all future updates."}
              </>
            ) : (
              "Define how the AI should write letters of this kind. Placeholders like {client_name} and {extra_notes} get filled in per-letter."
            )}
          </DialogDescription>
        </DialogHeader>
        <fieldset disabled={saving} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">Template name*</Label>
              <Input
                value={form.name || ""}
                onChange={(e) => updateField("name", e.target.value)}
                placeholder="e.g. WorkCover Progress Report"
                className="border-zinc-300 mt-1"
                data-testid="letter-tpl-name"
              />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">Description</Label>
              <Input
                value={form.description || ""}
                onChange={(e) => updateField("description", e.target.value)}
                placeholder="Optional — how you use this template"
                className="border-zinc-300 mt-1"
                data-testid="letter-tpl-description"
              />
            </div>
            <div>
              <Label className="text-2xs uppercase tracking-wider text-zinc-500">Recipient type</Label>
              <Select
                value={form.recipient_type || "custom"}
                onValueChange={(v) => updateField("recipient_type", v)}
              >
                <SelectTrigger className="border-zinc-300 mt-1" data-testid="letter-tpl-recipient-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gp">GP (uses the GP directory)</SelectItem>
                  <SelectItem value="ndis">NDIS / plan manager (no GP)</SelectItem>
                  <SelectItem value="custom">Custom (free-text recipient)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <div className="text-2xs uppercase tracking-[0.15em] text-zinc-500 font-semibold mb-2">
              Default data sources
            </div>
            <div className="rounded-md border border-zinc-200 p-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[
                ["intake", "Latest intake"],
                ["soap_notes", "Last 3 SOAP notes"],
                ["questionnaires", "Questionnaires"],
                ["sbn", "Strength by Numbers"],
                ["appointments", "Appointment history"],
              ].map(([k, label]) => (
                <div key={k} className="flex items-center justify-between gap-2 text-xs">
                  <span className="text-zinc-800">{label}</span>
                  <Switch
                    checked={!!form.default_sources?.[k]}
                    onCheckedChange={(v) => updateSource(k, v)}
                    data-testid={`letter-tpl-src-${k}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-2xs uppercase tracking-[0.15em] text-zinc-500 font-semibold">
                Header fields
              </div>
              <Button
                size="sm" variant="outline" className="h-7 text-xs border-zinc-300"
                onClick={addHeaderField}
                data-testid="letter-tpl-add-field"
              >
                <Plus className="h-3 w-3 mr-1" /> Add field
              </Button>
            </div>
            <p className="text-2xs text-zinc-500 mb-2">
              Each field becomes an editable box on the compose screen (e.g.
              "Claim number", "Insurer", "Date of injury"). Leave the ID
              blank to auto-generate one from the label.
            </p>
            {(form.header_fields || []).length === 0 ? (
              <p className="text-2xs italic text-zinc-400 px-1">No header fields.</p>
            ) : (
              <ul className="space-y-2">
                {form.header_fields.map((f, idx) => (
                  <li key={idx} className="grid grid-cols-[1fr_1fr_28px] gap-2 items-center">
                    <Input
                      value={f.label || ""}
                      onChange={(e) => updateHeaderField(idx, { label: e.target.value })}
                      placeholder="Label (shown to user)"
                      className="h-8 text-xs border-zinc-300"
                      data-testid={`letter-tpl-hf-label-${idx}`}
                    />
                    <Input
                      value={f.id || ""}
                      onChange={(e) => updateHeaderField(idx, { id: e.target.value })}
                      placeholder="ID (auto if blank)"
                      className="h-8 text-xs border-zinc-300 font-mono"
                      data-testid={`letter-tpl-hf-id-${idx}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeHeaderField(idx)}
                      className="text-zinc-400 hover:text-red-600"
                      data-testid={`letter-tpl-hf-del-${idx}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">
              System prompt (tells the AI how to write)*
            </Label>
            <Textarea
              value={form.system_prompt || ""}
              onChange={(e) => updateField("system_prompt", e.target.value)}
              rows={8}
              placeholder="e.g. 'You are an Accredited Exercise Physiologist drafting a WorkCover progress report. Assertive, evidence-based tone...'"
              className="mt-1 border-zinc-300 font-mono text-xs leading-relaxed"
              data-testid="letter-tpl-system-prompt"
            />
          </div>
          <div>
            <Label className="text-2xs uppercase tracking-wider text-zinc-500">
              User prompt template
            </Label>
            <Textarea
              value={form.user_prompt_template || ""}
              onChange={(e) => updateField("user_prompt_template", e.target.value)}
              rows={4}
              placeholder="Placeholders: {client_name}, {recipient_greeting}, {extra_notes}, {diagnosis_hint}"
              className="mt-1 border-zinc-300 font-mono text-xs leading-relaxed"
              data-testid="letter-tpl-user-prompt"
            />
            <p className="text-2xs text-zinc-500 mt-1">
              Placeholders: <code>{"{client_name}"}</code>, <code>{"{recipient_greeting}"}</code>, <code>{"{extra_notes}"}</code>, <code>{"{diagnosis_hint}"}</code>
            </p>
          </div>
        </fieldset>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="letter-tpl-save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}
            {saving ? "Saving…" : isBuiltin && !isCustomized ? "Save override" : "Save template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
